// Google Analytics 4 — minimal, privacy-safe integration.
// • Loads gtag.js once, only if REACT_APP_GA_MEASUREMENT_ID is set.
// • Sends page_view events on route changes.
// • Sends ONLY page_path (and a coarse page_title from the route map below) —
//   never names, emails, lottery numbers, ticket numbers, payment details, or
//   affiliate payout data.
// • Strips query strings and hashes so referral codes, session IDs, and OAuth
//   tokens never reach Google.
// • If the env var is missing, every export is a no-op so the app keeps working.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

// Coarse, sanitised route → human title map. We DO NOT pass dynamic IDs.
const ROUTE_TITLES = {
  "/": "Landing",
  "/login": "Login",
  "/register": "Register",
  "/verify-email": "Verify Email",
  "/verify-email-prompt": "Verify Email Prompt",
  "/dashboard": "Dashboard",
  "/predictions": "Predictions",
  "/tickets": "Games Tracking",
  "/analytics": "Analytics",
  "/subscription": "Subscription",
  "/subscription/success": "Subscription Success",
  "/settings": "Settings",
  "/reports": "Reports",
  "/admin": "Admin",
  "/admin-books": "Admin Books",
  "/contact": "Contact",
  "/earn": "Earn",
  "/winnings": "Community Winnings",
  "/community-stats": "Community Stats",
};

// Pathname sanitiser — drops anything that could be a user-identifiable token
// (e.g. /ref=ABC, /verify-email?token=xyz, /#session_id=...).
const sanitisePath = (pathname) => {
  if (!pathname) return "/";
  // Anything starting with /ref= is referral attribution; aggregate to /ref
  if (pathname.startsWith("/ref=")) return "/ref";
  return pathname;
};

let initialised = false;

export const initGA = () => {
  if (!MEASUREMENT_ID) return;
  if (initialised) return;
  if (typeof window === "undefined") return;

  // Inject the gtag.js script tag
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view=false → we drive page_view events manually on route change
  // anonymize_ip is the GA4 default but we set it explicitly for clarity.
  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  initialised = true;
};

export const trackPageView = (pathname) => {
  if (!MEASUREMENT_ID) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const path = sanitisePath(pathname);
  const title = ROUTE_TITLES[path] || "Other";
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: `${window.location.origin}${path}`,
  });
};
